import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import GavelIcon from '@mui/icons-material/Gavel';
import TuneIcon from '@mui/icons-material/Tune';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EntityDetailDrawer from '../EntityDetailDrawer';
import ValidationMessages, { ValidationProblem } from './ValidationMessages';
import { ScopeLevelChip, StatusChip } from './OntologyBrowser';
import { Field } from './DetailPrimitives';
import {
    BINDS_VIA_LABEL, OntologyKind, VALIDATE_RESOURCE, conceptTypeLabel, formatValue,
    sourceTypeLabel,
} from './ontologyFormat';
import {
    apiForKind, normalizeDocument, normalizeValidation, validateDocument,
} from '../../api/ontologyApi';
import { dsVar } from '../../theme/tokens';

/**
 * A peek at one ontology record, without leaving the list.
 *
 * Deliberately NOT a replacement for OntologyDetailPage. Two things stay on the full page:
 *
 *   - the BULK. A concept carries guidance in eight parts, measurement, tuning, its
 *     thresholds, its relations, its usage across templates and its sources. At 440px that
 *     is a scroll nobody reads;
 *   - the LIFECYCLE ACTIONS. Approve, Supersede, Delete and Customise change what the
 *     detection engine applies, within a cache TTL for a parameter. A drawer reads as a
 *     glance, and a glance is the wrong place to put an irreversible action. There is one
 *     way out of here to act on a record, and it is "Open full record".
 *
 * The row that opened this already carries the label, code, definition, scope and status,
 * so all of that renders immediately from `doc`. The fetch below only fills in what a list
 * row does not carry, and never blanks what is already on screen.
 */

const KIND_ICON: Record<OntologyKind, React.ReactNode> = {
    concepts: <PsychologyIcon />,
    policies: <GavelIcon />,
    parameters: <TuneIcon />,
};

interface OntologyPreviewDrawerProps {
    open: boolean;
    kind: OntologyKind;
    code: string | null;
    /** The row that was clicked — everything a listing already knows. */
    row: Record<string, any> | null;
    onClose: () => void;
}

/** The one-line summary each kind leads with. */
const descriptionOf = (kind: OntologyKind, doc: Record<string, any>): string => {
    if (kind === 'concepts') return doc.guidance?.what_it_means || doc.definition || '';
    if (kind === 'policies') return doc.narrative || '';
    // A parameter has no narrative at all — it is a value. Say what it is and what it
    // binds to, rather than handing the markdown renderer an empty string.
    const value = formatValue(doc.value, doc.unit);
    const binds = doc.concept_code
        ? `Applies to **${doc.concept_code}**.`
        : '_Governs nothing — this threshold names no concept._';
    return `**${value}**\n\n${binds}`;
};

const titleOf = (kind: OntologyKind, doc: Record<string, any>, code: string): string => {
    if (kind === 'concepts') return doc.preferred_label || code;
    if (kind === 'policies') return doc.title || code;
    return doc.parameter_code || code;
};

const OntologyPreviewDrawer: React.FC<OntologyPreviewDrawerProps> = ({
    open, kind, code, row, onClose,
}) => {
    const navigate = useNavigate();

    // Seeded from the clicked row so the drawer has content on the first frame. The fetch
    // merges over it; it never replaces it with null, so nothing flashes empty.
    const [detail, setDetail] = useState<Record<string, any> | null>(null);
    const [warnings, setWarnings] = useState<ValidationProblem[]>([]);

    useEffect(() => {
        setDetail(null);
        setWarnings([]);
        if (!open || !code) return undefined;

        const api = apiForKind(kind);
        if (!api) return undefined;

        const controller = new AbortController();
        let active = true;

        (async () => {
            try {
                const res = await api.get(code, { signal: controller.signal });
                if (!active) return;
                const document = normalizeDocument(res, kind);
                setDetail(document);

                // The same warning the detail page shows — "your threshold is looser than
                // statute" belongs in the glance, not only behind another click.
                try {
                    const validation = await validateDocument(
                        VALIDATE_RESOURCE[kind], document, { signal: controller.signal });
                    if (active) setWarnings(normalizeValidation(validation).warnings);
                } catch {
                    if (active) setWarnings([]);
                }
            } catch {
                // The row's own fields stay on screen; a failed enrichment is not worth an
                // error state over content the user can already read.
                if (active) setDetail(null);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [open, kind, code]);

    const doc = { ...(row ?? {}), ...(detail ?? {}) };
    if (!open || !code) return null;

    return (
        <EntityDetailDrawer
            open={open}
            onClose={onClose}
            docked
            // The Risk Library carries a category rail as well as the table, so there is
            // less room to give away than on a plain card grid.
            dockedMinWidth={1280}
            icon={KIND_ICON[kind]}
            subtitle={code}
            title={titleOf(kind, doc, code)}
            description={descriptionOf(kind, doc)}
            banner={(
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <ScopeLevelChip scopeLevel={doc.scope_level} />
                    <StatusChip status={doc.status} />
                </Box>
            )}
            actions={(
                <Button
                    onClick={() => navigate(`/ontology/${kind}/${encodeURIComponent(code)}`)}
                    endIcon={<OpenInFullIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        textTransform: 'none', fontWeight: 600,
                        backgroundColor: dsVar('accent'), color: dsVar('on-accent'),
                        '&:hover': { backgroundColor: '#F5D149' },
                    }}
                >
                    Open full record
                </Button>
            )}
        >
            {warnings.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <ValidationMessages warnings={warnings} />
                </Box>
            )}

            <Box sx={{ mt: 2 }}>
                {kind === 'concepts' && (
                    <>
                        <Field label="Type" value={conceptTypeLabel(doc.concept_type)} />
                        {doc.guidance?.what_it_means && doc.definition && (
                            <Field label="Formal definition" value={doc.definition} />
                        )}
                        <Field label="Detected by" value={(doc.detected_by || []).join(', ')} />
                        <Field label="Also known as" value={(doc.synonyms || []).join(', ')} />
                    </>
                )}

                {kind === 'policies' && (
                    <>
                        <Field label="Section" value={doc.section} />
                        <Field label="Owner" value={doc.owner_role} />
                        <Field label="Regulator" value={doc.regulator} />
                        <Field
                            label="Governs"
                            value={doc.governs_concepts?.length
                                ? `${doc.governs_concepts.length} terms`
                                : undefined}
                        />
                    </>
                )}

                {kind === 'parameters' && (
                    <>
                        <Field label="Set by policy" value={doc.policy_code} />
                        <Field label="Binds" value={BINDS_VIA_LABEL[doc.binds_via] ?? doc.binds_via} />
                        <Field label="Source" value={sourceTypeLabel(doc.source_type)} />
                        <Field label="Note" value={doc.note} />
                    </>
                )}
            </Box>

            {/* Says plainly that this is a glance, so a user who came looking for Approve
                knows where it went rather than concluding it is missing. */}
            <Typography
                sx={{
                    mt: 3, pt: 2, borderTop: `1px solid ${dsVar('line')}`,
                    fontSize: '0.75rem', color: dsVar('fg-muted'), lineHeight: 1.6,
                }}
            >
                Versions, where this is used, and the actions you can take on it are on the
                full record.
            </Typography>
        </EntityDetailDrawer>
    );
};

export default OntologyPreviewDrawer;
